import type {
  AccessTokenDto,
  AuthOrganizationDto,
  AuthSessionDto,
  AuthUserDto,
  IdentityDto,
  ImpersonationDto,
  Language,
} from '@osteopro/shared';
import { api, ApiError, isApiError, setAccessTokenReader, unwrap } from './api.js';

/**
 * Magasin d'authentification. Le jeton d'accès vit **en mémoire uniquement** : jamais dans
 * `localStorage`, où un script injecté le lirait. Sa persistance entre deux chargements de page
 * est assurée par le cookie httpOnly de rafraîchissement, que le navigateur seul manipule.
 *
 * Écrit en magasin externe (`useSyncExternalStore`) plutôt qu'en contexte React, parce que le
 * jeton doit aussi être lisible par le client HTTP et par la relance sur 401, hors de tout rendu.
 */

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthSnapshot {
  status: AuthStatus;
  user: AuthUserDto | null;
  organizations: AuthOrganizationDto[];
  activeOrganizationId: string | null;
  /**
   * Séance « Voir comme » en cours, nulle sinon. Pendant la séance, `user` et `organizations` sont
   * ceux du compte regardé : l'écran se construit comme pour lui, en lecture seule.
   */
  impersonation: ImpersonationDto | null;
}

const ANONYMOUS: AuthSnapshot = {
  status: 'anonymous',
  user: null,
  organizations: [],
  activeOrganizationId: null,
  impersonation: null,
};

/** Marge avant expiration : on rafraîchit une minute avant que le jeton ne tombe. */
const REFRESH_MARGIN_SECONDS = 60;

let snapshot: AuthSnapshot = { ...ANONYMOUS, status: 'loading' };
let accessToken: string | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let inFlightRefresh: Promise<boolean> | null = null;
let clearCaches: () => void = () => undefined;

const listeners = new Set<() => void>();

function publish(next: AuthSnapshot): void {
  snapshot = next;
  for (const listener of listeners) listener();
}

export function subscribeAuth(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAuthSnapshot(): AuthSnapshot {
  return snapshot;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Branché par les fournisseurs : vider le cache de requêtes fait partie de la déconnexion. */
export function setCacheResetter(reset: () => void): void {
  clearCaches = reset;
}

setAccessTokenReader(getAccessToken);

function cancelScheduledRefresh(): void {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function scheduleRefresh(expiresIn: number): void {
  cancelScheduledRefresh();
  const delay = Math.max(expiresIn - REFRESH_MARGIN_SECONDS, 5) * 1000;
  refreshTimer = setTimeout(() => {
    void refreshAccessToken();
  }, delay);
}

function applySession(session: AuthSessionDto): void {
  accessToken = session.accessToken;
  scheduleRefresh(session.expiresIn);
  publish({
    status: 'authenticated',
    user: session.user,
    organizations: session.organizations,
    activeOrganizationId: session.activeOrganizationId,
    impersonation: session.impersonation,
  });
}

/**
 * Entrer dans une séance ou en sortir change d'identité : le cache de requêtes est vidé dans le
 * même geste, pour qu'aucune donnée d'un compte ne s'affiche sous l'autre.
 */
function applyIdentity(identity: IdentityDto): void {
  const switched = snapshot.impersonation?.startedAt !== identity.impersonation?.startedAt;
  publish({
    status: 'authenticated',
    user: identity.user,
    organizations: identity.organizations,
    activeOrganizationId: identity.activeOrganizationId,
    impersonation: identity.impersonation,
  });
  if (switched) clearCaches();
}

function applyAccessToken(token: AccessTokenDto): void {
  accessToken = token.accessToken;
  scheduleRefresh(token.expiresIn);
}

function forgetSession(): void {
  cancelScheduledRefresh();
  accessToken = null;
  publish({ ...ANONYMOUS });
}

/**
 * Rotation silencieuse. Un seul appel en vol à la fois : deux requêtes qui prennent un 401
 * ensemble ne doivent pas faire tourner le jeton deux fois, ce que le serveur lirait comme un
 * rejeu et qui révoquerait toute la famille de sessions.
 */
async function refreshAccessToken(): Promise<boolean> {
  inFlightRefresh ??= (async () => {
    try {
      applyAccessToken(await unwrap(await api.api.auth.refresh.$post()));
      return true;
    } catch {
      forgetSession();
      return false;
    } finally {
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
}

/**
 * Exécute un appel authentifié, avec **une seule** relance après rafraîchissement sur 401.
 * Toutes les requêtes des hooks passent par là ; l'expiration du jeton devient invisible.
 */
export async function runAuthed<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request();
  } catch (error) {
    if (!isApiError(error) || error.status !== 401) throw error;
    if (!(await refreshAccessToken())) throw error;
    return await request();
  }
}

/** Démarrage de l'application : un 401 signifie simplement « pas connecté ». */
export async function bootstrapAuth(): Promise<void> {
  if (await refreshAccessToken()) {
    try {
      applyIdentity(await unwrap(await api.api.auth.me.$get()));
      return;
    } catch {
      forgetSession();
      return;
    }
  }
  publish({ ...ANONYMOUS });
}

export interface LoginInput {
  email: string;
  password: string;
  totpCode?: string;
}

export async function login(input: LoginInput): Promise<void> {
  const session = await unwrap(await api.api.auth.login.$post({ json: input }));
  applySession(session);
}

export interface RegisterInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  organizationName?: string;
  phone?: string;
  /** Numéro d'autorisation d'exercer. Va dans le profil praticien du cabinet créé. */
  authorizationNumber?: string;
  /**
   * Langue du cabinet et du compte à l'inscription. Elle commande le courriel de vérification,
   * le catalogue préchargé et la langue par défaut du cabinet. Le serveur replie sur `fr` si
   * elle manque.
   */
  language?: Language;
}

export async function register(input: RegisterInput): Promise<void> {
  const session = await unwrap(await api.api.auth.register.$post({ json: input }));
  applySession(session);
}

export interface AcceptInvitationInput {
  /** Jeton du lien reçu par courriel : il vaut autorisation à lui seul, il n'y a pas de session. */
  token: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  totpCode?: string;
}

/**
 * Accepter une invitation ouvre une session exactement comme une connexion : le serveur renvoie la
 * même réponse, on passe donc par le même `applySession` — jeton en mémoire, rotation programmée,
 * instantané publié. Rien n'est recopié ici, sinon les deux chemins divergeraient au premier
 * changement du cycle de vie de session.
 */
export async function acceptInvitation({ token, ...body }: AcceptInvitationInput): Promise<void> {
  const session = await unwrap(
    await api.api.invitations[':token'].accept.$post({ param: { token }, json: body }),
  );
  applySession(session);
}

/** La déconnexion vide tout, même si le serveur refuse : l'état local ne doit rien garder. */
export async function logout(): Promise<void> {
  try {
    await runAuthed(async () => unwrap(await api.api.auth.logout.$post()));
  } catch {
    // Session déjà révoquée côté serveur : le nettoyage local reste la seule chose qui compte.
  } finally {
    forgetSession();
    clearCaches();
  }
}

/** Déconnecte toutes les autres sessions puis la session courante. */
export async function logoutEverywhere(): Promise<void> {
  try {
    await runAuthed(async () => unwrap(await api.api.auth['logout-all'].$post()));
  } finally {
    forgetSession();
    clearCaches();
  }
}

export async function switchOrganization(organizationId: string): Promise<void> {
  const token = await runAuthed(async () =>
    unwrap(await api.api.auth['switch-organization'].$post({ json: { organizationId } })),
  );
  applyAccessToken(token);
  const identity = await runAuthed(async () => unwrap(await api.api.auth.me.$get()));
  applyIdentity(identity);
  clearCaches();
}

/** Recharge l'identité après une action qui la modifie (activation du second facteur…). */
export async function reloadIdentity(): Promise<void> {
  applyIdentity(await runAuthed(async () => unwrap(await api.api.auth.me.$get())));
}

/** Ouvre une séance « Voir comme » sur ce compte. Le serveur décide qui peut, et qui. */
export async function startImpersonation(userId: string): Promise<void> {
  await runAuthed(async () =>
    unwrap(await api.api.platform.users[':id'].impersonate.$post({ param: { id: userId } })),
  );
  await reloadIdentity();
}

/** Ferme la séance : l'identité redevient celle de l'administrateur. */
export async function endImpersonation(): Promise<void> {
  await runAuthed(async () => unwrap(await api.api.auth.impersonation.end.$post()));
  await reloadIdentity();
}

/**
 * Branché sur les deux caches de requêtes : une séance échue répond `impersonation_ended` à la
 * première requête qui le constate. L'identité est relue — celle de l'administrateur.
 */
export function reportImpersonationEnded(error: unknown): void {
  if (!isApiError(error) || error.code !== 'impersonation_ended') return;
  void reloadIdentity().catch(() => undefined);
}

/** Réservé aux tests : remet le magasin dans son état de démarrage. */
export function resetAuthStoreForTests(): void {
  cancelScheduledRefresh();
  inFlightRefresh = null;
  accessToken = null;
  clearCaches = () => undefined;
  publish({ ...ANONYMOUS, status: 'loading' });
}

export { ApiError };
